import React, { useEffect, useMemo, useState } from 'react';
/**
 * 债权人步骤组件
 * 职责：
 * - 管理个人/企业债权人输入项（互斥），统一字段映射
 * - 支持从实名认证记录自动填充（个人按钮、企业下拉选项）
 * - 保留共享字段（电话、邮箱、省市、地址）在类型切换时不清空
 */
import { Form, Input, Radio, Button, Space, Select, message } from 'antd';
import provinces from 'china-division/dist/provinces.json';
import cities from 'china-division/dist/cities.json';
import { getVerificationList } from '../../../../services/verification';

const CreditorStep = ({ value, onChange, onNext, onPrev }) => {
  const [form] = Form.useForm();
  const [verifiedList, setVerifiedList] = useState([]);
  const [loadingVerify, setLoadingVerify] = useState(false);

  useEffect(() => {
    form.setFieldsValue(value.creditor);
  }, [value.creditor, form]);

  const handleValuesChange = (_, allValues) => {
    onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...allValues } }));
  };

  // 加载当前登录用户的实名认证记录列表（含个人与企业）
  const loadVerified = async () => {
    if (loadingVerify) return verifiedList;
    try {
      setLoadingVerify(true);
      const res = await getVerificationList();
      const list = Array.isArray(res.data) ? res.data : (res.data?.records || []);
      const finalList = list || [];
      setVerifiedList(finalList);
      return finalList;
    } catch (e) {
      message.error(e?.message || '加载认证信息失败');
      return [];
    } finally {
      setLoadingVerify(false);
    }
  };

  const latestPersonal = useMemo(() => {
    const arr = (verifiedList || []).filter(it => String(it.verificationType) === 'P' && (it.isVerified === true || String(it.status) === 'APPROVED'));
    if (arr.length === 0) return null;
    return arr.sort((a, b) => new Date(b.createTime || 0) - new Date(a.createTime || 0))[0];
  }, [verifiedList]);

  const enterpriseOptions = useMemo(() => {
    const arr = (verifiedList || []).filter(it => String(it.verificationType) === 'E' && (it.isVerified === true || String(it.status) === 'APPROVED'));
    return arr.map((it, idx) => ({
      value: idx,
      label: `${it.companyName || ''}${it.socialCreditCode ? `（${it.socialCreditCode}）` : ''}`,
      record: it,
    }));
  }, [verifiedList]);

  // 使用个人实名认证信息填充：选择最新一条并写入表单
  const handleUsePersonalVerified = async () => {
    const list = await loadVerified();
    const arr = (list || []).filter(it => String(it.verificationType) === 'P' && (it.isVerified === true || String(it.status) === 'APPROVED'));
    const latest = arr.length > 0 ? arr.sort((a, b) => new Date(b.createTime || 0) - new Date(a.createTime || 0))[0] : null;
    if (!latest) {
      message.warning('未查询到已完成的个人认证，请先完成个人认证或手动填写');
      return;
    }
    const next = {
      creditor_type: 'P',
      name: latest.realName || '',
      id_card_no: latest.idCardNo || '',
      phone: latest.phoneNumber || '',
    };
    form.setFieldsValue(next);
    onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...next } }));
    message.success('已加载个人认证信息，可继续更改');
  };

  // 使用企业实名认证信息填充：选中指定企业后写入表单
  const handleUseEnterpriseVerified = async (optValue) => {
    if (verifiedList.length === 0) await loadVerified();
    const opt = enterpriseOptions.find(o => o.value === optValue);
    if (!opt) return;
    const r = opt.record || {};
    const next = {
      creditor_type: 'E',
      company_name: r.companyName || '',
      social_credit_code: r.socialCreditCode || '',
      legal_person_name: r.legalPersonName || '',
      legal_person_id_no: r.legalPersonIdCard || '',
      phone: r.contactPhone || '',
    };
    form.setFieldsValue(next);
    onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...next } }));
    message.success('已加载企业认证信息，可继续更改');
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      onNext();
    } catch (e) {}
  };

  const isPersonal = value.creditor.creditor_type === 'P';

  const provinceOptions = provinces.map(p => ({ value: p.name, label: p.name, code: p.code }));
  const cityOptionsMap = provinces.reduce((acc, p) => {
    const list = cities.filter(c => c.provinceCode === p.code).map(c => ({ value: c.name, label: c.name }));
    acc[p.name] = list;
    return acc;
  }, {});
  const currentProvince = Form.useWatch('province', form);
  // 切换类型互斥处理 + 自动填充（字段为空且存在认证记录时）
  useEffect(() => {
    (async () => {
      const type = value?.creditor?.creditor_type;
      // 互斥清空：选择个人时清空企业专属字段；选择企业时清空个人专属字段
      if (type === 'P') {
        const clearEnterprise = {
          company_name: undefined,
          social_credit_code: undefined,
          legal_person_name: undefined,
          legal_person_id_no: undefined,
        };
        form.setFieldsValue(clearEnterprise);
        onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...clearEnterprise } }));
      } else if (type === 'E') {
        const clearPersonal = {
          name: undefined,
          id_card_no: undefined,
        };
        form.setFieldsValue(clearPersonal);
        onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...clearPersonal } }));
      }
      if (type === 'P') {
        const name = form.getFieldValue('name');
        const id = form.getFieldValue('id_card_no');
        if (!name && !id) {
          const list = await loadVerified();
          const arr = (list || []).filter(it => String(it.verificationType) === 'P' && (it.isVerified === true || String(it.status) === 'APPROVED'));
          const latest = arr.length > 0 ? arr.sort((a, b) => new Date(b.createTime || 0) - new Date(a.createTime || 0))[0] : null;
          if (latest) {
            const next = { creditor_type: 'P', name: latest.realName || '', id_card_no: latest.idCardNo || '' };
            form.setFieldsValue(next);
            onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...next } }));
          }
        }
      } else if (type === 'E') {
        const cname = form.getFieldValue('company_name');
        const sc = form.getFieldValue('social_credit_code');
        if (!cname && !sc) {
          const list = await loadVerified();
          const arr = (list || []).filter(it => String(it.verificationType) === 'E' && (it.isVerified === true || String(it.status) === 'APPROVED'));
          const r = arr.length > 0 ? arr.sort((a, b) => new Date(b.createTime || 0) - new Date(a.createTime || 0))[0] : null;
          if (r) {
            const next = {
              creditor_type: 'E',
              company_name: r.companyName || '',
              social_credit_code: r.socialCreditCode || '',
              legal_person_name: r.legalPersonName || '',
              legal_person_id_no: r.legalPersonIdCard || '',
              phone: r.contactPhone || '',
            };
            form.setFieldsValue(next);
            onChange(prev => ({ ...prev, creditor: { ...prev.creditor, ...next } }));
          }
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.creditor?.creditor_type]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={value.creditor}
      onValuesChange={handleValuesChange}
    >
      <Form.Item label="债权人类型" name="creditor_type" rules={[{ required: true, message: '请选择债权人类型' }]}> 
        <Radio.Group>
          <Radio value="P">个人</Radio>
          <Radio value="E">企业</Radio>
        </Radio.Group>
      </Form.Item>

      {isPersonal ? (
        <>
          <Form.Item>
            <Space>
              <Button onClick={handleUsePersonalVerified} loading={loadingVerify}>使用个人认证信息</Button>
            </Space>
          </Form.Item>
          <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请填写姓名' }]}> 
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="身份证号" name="id_card_no" rules={[{ required: true, message: '请填写身份证号' }]}> 
            <Input maxLength={50} />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item label="使用企业认证信息">
            <Select
              allowClear
              loading={loadingVerify}
              placeholder="选择已认证企业"
              options={enterpriseOptions}
              onDropdownVisibleChange={(open) => { if (open && verifiedList.length === 0) loadVerified(); }}
              onChange={handleUseEnterpriseVerified}
            />
          </Form.Item>
          <Form.Item label="企业名称" name="company_name" rules={[{ required: true, message: '请填写企业名称' }]}> 
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item label="统一社会信用代码" name="social_credit_code" rules={[{ required: true, message: '请填写统一社会信用代码' }]}> 
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item label="法定代表人姓名" name="legal_person_name">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="法定代表人身份证号" name="legal_person_id_no"> 
            <Input maxLength={50} />
          </Form.Item>
        </>
      )}

      <Form.Item label="联系电话" name="phone"> 
        <Input maxLength={20} />
      </Form.Item>
      <Form.Item label="邮箱" name="email"> 
        <Input maxLength={100} />
      </Form.Item>
      <Form.Item label="省" name="province"> 
        <Select 
          allowClear 
          options={provinceOptions}
          onChange={(val) => {
            form.setFieldsValue({ province: val, city: undefined });
            onChange(prev => ({ ...prev, creditor: { ...prev.creditor, province: val, city: '' } }));
          }}
        />
      </Form.Item>
      <Form.Item label="市" name="city"> 
        <Select 
          allowClear 
          disabled={!currentProvince}
          options={currentProvince ? (cityOptionsMap[currentProvince] || []) : []}
          onChange={(val) => {
            onChange(prev => ({ ...prev, creditor: { ...prev.creditor, city: val } }));
          }}
        />
      </Form.Item>
      <Form.Item label="详细地址" name="address"> 
        <Input maxLength={255} />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button onClick={onPrev}>上一步</Button>
          <Button type="primary" onClick={handleNext}>下一步</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default CreditorStep;
